import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import AddProduct from "../components/AddProduct";
import { can, Permissions } from "../auth/authorization";
import ProductDetails from "../components/ProductDetails";
import AuthContext from "../AuthContext";
import { apiFetch } from "../api/client";
import { useNotification } from "../components/Notifications";
import { mapApiErrorToNotification } from "../utils/notificationHelpers";

function Inventory() {
  const [showProductModal, setShowProductModal] = useState(false);
  const [editInitialProduct, setEditInitialProduct] = useState(null);
  const [products, setAllProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [updatePage, setUpdatePage] = useState(true);
  const [planInfo, setPlanInfo] = useState(null);
  const [remainingSlots, setRemainingSlots] = useState(null);
  const [stores, setAllStores] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const notify = useNotification();

  const authContext = useContext(AuthContext);

  // ✅ Fetch all products from API
  const fetchProductsData = async () => {
    try {
      const res = await apiFetch(`/api/product`);
      if (!res.ok) {
        const parsed = await res.json().catch(() => null);
        const note = mapApiErrorToNotification(parsed, res.status);
        notify("error", note);
        return;
      }
      const data = await res.json();
      const arr = Array.isArray(data) ? data : (data?.data || data?.Data || []);
      const parseNumber = (v) => {
        if (v === null || v === undefined) return 0;
        const s = String(v).replace(/[^0-9.-]+/g, "");
        const n = Number(s);
        return Number.isNaN(n) ? 0 : n;
      };

      const normalize = (p) => ({
        id: p.productId || p.ProductId || p._id || p.ProductId,
        name: p.name || p.Name || "",
        description: p.description || p.Description || "",
        purchaseCost: parseNumber(p.purchaseCost ?? p.PurchaseCost ?? 0),
        salePrice: parseNumber(p.salePrice ?? p.SalePrice ?? 0),
        category: p.category || p.Category || p.categoryName || p.CategoryName || "",
        categoryId: p.categoryId || p.CategoryId || null,
        vendor: p.vendor || p.Vendor || p.vendorName || p.VendorName || "",
        vendorId: p.vendorId || p.VendorId || null,
        stock: p.stock ?? p.Stock ?? 0,
      });
      setAllProducts((arr || []).map(normalize));
    } catch (err) {
      console.log("Error fetching products:", err);
    }
  };

  // ✅ Fetch all vendors (stores)
  const fetchSalesData = async () => {
    try {
      const res = await apiFetch(`/api/vendor`);
      if (!res.ok) return;
      const data = await res.json();
      const arr = Array.isArray(data) ? data : (data?.data || data?.Data || []);
      setAllStores(arr || []);
    } catch (err) {
      console.log("Error fetching stores:", err);
    }
  };

  // ✅ Fetch data when page loads or updates
  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchProductsData(),
      fetchSalesData(),
      fetchPlanInfo(),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePage, authContext.user]);

  async function fetchPlanInfo() {
    try {
      const res = await apiFetch('/api/subscription');
      if (!res.ok) return;
      const data = await res.json().catch(() => null);

      // tolerant extraction for shape { plan: { ... } }
      let plan = data?.plan || data?.data?.plan || data?.data || data?.Data?.plan || data?.Data || data || null;
      if (Array.isArray(plan) && plan.length > 0) plan = plan[0];
      if (!plan) return;

      const normalized = {
        id: plan.planId ?? plan.PlanId ?? plan.id ?? null,
        name: plan.name ?? plan.Name ?? plan.Slug ?? plan.slug ?? null,
        slug: plan.slug ?? plan.Slug ?? plan.name ?? plan.Name ?? null,
        itemLimit: plan.itemLimit ?? plan.ItemLimit ?? plan.item_limit ?? null,
        raw: plan,
      };
      setPlanInfo(normalized);
    } catch (e) {
      console.error('Failed to fetch plan info', e);
    }
  }

  // update remaining slots whenever products or planInfo change
  useEffect(() => {
    if (planInfo && planInfo.itemLimit != null) {
      const rem = Math.max(0, (planInfo.itemLimit || 0) - (products?.length || 0));
      setRemainingSlots(rem);
    } else {
      setRemainingSlots(null);
    }
  }, [products, planInfo]);

  // ✅ Search products (client-side)
  const fetchSearchData = async () => {
    if (!searchTerm) return fetchProductsData();
    try {
      const filtered = (products || []).filter((p) =>
        (p.name || "").toLowerCase().includes(searchTerm.toLowerCase())
      );
      setAllProducts(filtered);
    } catch (err) {
      console.log("Error searching products", err);
    }
  };

  // ✅ Modal control handlers
  const addProductModalSetting = (initial = null) => {
    // if there is an item limit and no remaining slots, prevent opening modal for adding new products
    if (!initial && planInfo && planInfo.itemLimit != null && remainingSlots === 0) {
      notify("error", { title: "Limit reached", text: "You have reached your product limit. Please upgrade your plan to add more items." });
      return;
    }
    setEditInitialProduct(initial);
    setShowProductModal(!showProductModal);
  };

  const showProductDetails = async (product) => {
    // fetch fresh product from API to ensure stock is up-to-date
    const id = product.id || product.productId || product.ProductId || product._id;
    if (!id) {
      // fallback to local product if no id
      const p = {
        id: product.id || product.ProductId || product._id,
        name: product.name || product.Name,
        vendor: product.vendor || product.Vendor || product.vendorName || product.VendorName,
        category: product.category || product.Category || product.categoryName || product.CategoryName,
        stock: product.stock || product.Stock || 0,
        description: product.description || product.Description || "",
        purchaseCost: product.purchaseCost ?? product.PurchaseCost ?? 0,
        salePrice: product.salePrice ?? product.SalePrice ?? 0,
      };
      setSelectedProduct(p);
      setShowDetailsModal(true);
      return;
    }

    try {
      const res = await apiFetch(`/api/product/${id}`);
      if (!res.ok) {
        // if fetch fails, fallback to provided product
        const p = {
          id: product.id || product.ProductId || product._id,
          name: product.name || product.Name,
          vendor: product.vendor || product.Vendor || product.vendorName || product.VendorName,
          category: product.category || product.Category || product.categoryName || product.CategoryName,
          stock: product.stock || product.Stock || 0,
          description: product.description || product.Description || "",
          purchaseCost: product.purchaseCost ?? product.PurchaseCost ?? 0,
          salePrice: product.salePrice ?? product.SalePrice ?? 0,
        };
        setSelectedProduct(p);
        setShowDetailsModal(true);
        return;
      }

      const parsed = await res.json().catch(() => null);
      const dto = parsed?.data || parsed?.Data || parsed;
      const p = {
        id: dto.productId || dto.productId || dto.ProductId || dto.productId || id,
        name: dto.name || dto.Name || "",
        vendor: dto.vendorName || dto.VendorName || dto.vendor || dto.Vendor || "",
        category: dto.categoryName || dto.CategoryName || dto.category || dto.Category || "",
        stock: dto.stock ?? dto.Stock ?? 0,
        description: dto.description || dto.Description || "",
        purchaseCost: dto.purchaseCost ?? dto.PurchaseCost ?? 0,
        salePrice: dto.salePrice ?? dto.SalePrice ?? 0,
      };
      setSelectedProduct(p);
      setShowDetailsModal(true);
    } catch (err) {
      console.error("Error fetching product details:", err);
      const p = {
        id: product.id || product.ProductId || product._id,
        name: product.name || product.Name,
        vendor: product.vendor || product.Vendor || product.vendorName || product.VendorName,
        category: product.category || product.Category || product.categoryName || product.CategoryName,
        stock: product.stock || product.Stock || 0,
        description: product.description || product.Description || "",
        purchaseCost: product.purchaseCost ?? product.PurchaseCost ?? 0,
        salePrice: product.salePrice ?? product.SalePrice ?? 0,
      };
      setSelectedProduct(p);
      setShowDetailsModal(true);
    }
  };

  // ✅ Delete product
  const deleteItem = async (id) => {
    try {
      const res = await apiFetch(`/api/product/${id}`, { method: "DELETE" });
      if (!res.ok) {
        const parsed = await res.json().catch(() => null);
        const note = mapApiErrorToNotification(parsed, res.status);
        notify("error", note);
        return;
      }
      setUpdatePage(!updatePage);
      notify("success", { title: "Deleted", text: "Product deleted." });
    } catch (err) {
      console.log("Error deleting product:", err);
    }
  };

  // ✅ Handle search
  const handleSearchTerm = (e) => {
    setSearchTerm(e.target.value);
    // debounce not implemented; simple immediate filter
    fetchSearchData();
  };

  // ✅ Refresh page data after add/update
  const handlePageUpdate = () => setUpdatePage(!updatePage);

  const formatCurrency = (v) => {
    if (v === null || v === undefined) return "₦0";
    // remove non-numeric characters (commas, currency symbols) then parse
    const s = String(v).replace(/[^0-9.-]+/g, "");
    const num = Number(s);
    if (Number.isNaN(num)) return "₦0";
    return `₦${num.toLocaleString()}`;
  };

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center">
      <div className="flex flex-col gap-5 w-11/12">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
        <>
        {/* Overall Inventory Section */}
        <div className="bg-white rounded p-3">
          <span className="font-semibold px-4">Overall Inventory</span>
          <div className="flex flex-col md:flex-row justify-center items-center">
            <div className="flex flex-col p-10 w-full md:w-3/12">
              <span className="font-semibold text-blue-600 text-base">Total Products</span>
              <span className="font-semibold text-gray-600 text-base">{products.length}</span>
              <span className="font-thin text-gray-400 text-xs">Last 7 days</span>
            </div>
            <div className="flex flex-col gap-3 p-10 w-full md:w-3/12 sm:border-y-2 md:border-x-2 md:border-y-0">
              <span className="font-semibold text-yellow-600 text-base">Stores</span>
              <div className="flex gap-8">
                <div className="flex flex-col">
                  <span className="font-semibold text-gray-600 text-base">{stores.length}</span>
                  <span className="font-thin text-gray-400 text-xs">Total vendors</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 p-10 w-full md:w-3/12 sm:border-y-2 md:border-x-2 md:border-y-0">
              <span className="font-semibold text-purple-600 text-base">Inventory Value</span>
              <div className="flex gap-8">
                <div className="flex flex-col">
                  {/* total purchase cost = sum(purchaseCost * stock) */}
                  <span className="font-semibold text-gray-600 text-base">{formatCurrency(products.reduce((a, p) => a + ((p.purchaseCost || 0) * (p.stock || 0)), 0))}</span>
                  <span className="font-thin text-gray-400 text-xs">Total purchase cost</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 p-10 w-full md:w-3/12 border-y-2 md:border-x-2 md:border-y-0">
              <span className="font-semibold text-red-600 text-base">Low Stocks</span>
              <div className="flex gap-8">
                <div className="flex flex-col">
                  {/* low stock count */}
                  <span className="font-semibold text-gray-600 text-base">{products.filter(p => (p.stock ?? 0) <= 5).length}</span>
                  <span className="font-thin text-gray-400 text-xs">Items low in stock</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        {showProductModal && can(Permissions.Add) && (
          <AddProduct
            addProductModalSetting={addProductModalSetting}
            handlePageUpdate={handlePageUpdate}
            initial={editInitialProduct}
          />
        )}
        {showDetailsModal && (
          <ProductDetails
            product={selectedProduct}
            onClose={() => setShowDetailsModal(false)}
          />
        )}

        {/* Products Table */}
        <div className="overflow-x-auto rounded-lg border bg-white border-gray-200">
          <div className="flex justify-between items-center pt-5 pb-3 px-3">
            <div className="flex gap-4 items-center">
              <span className="font-bold text-gray-800 text-base">Products</span>
              <div className="flex items-center px-2 border rounded-md">
                <img
                  alt="search-icon"
                  className="w-5 h-5"
                  src={require("../assets/search-icon.png")}
                />
                <input
                  className="border-none outline-none text-xs px-1 w-32"
                  type="text"
                  placeholder="Search here"
                  value={searchTerm}
                  onChange={handleSearchTerm}
                />
              </div>
            </div>

            {can(Permissions.Add) && (
              <div className="flex items-center gap-3">
                <button
                  className={`text-white font-medium px-4 py-2 text-xs rounded ${planInfo && planInfo.itemLimit != null && products.length >= planInfo.itemLimit ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                  onClick={() => addProductModalSetting(null)}
                  disabled={planInfo && planInfo.itemLimit != null && products.length >= planInfo.itemLimit}
                >
                  {planInfo && planInfo.itemLimit != null && products.length >= planInfo.itemLimit ? 'Limit reached' : '+ Add Product'}
                </button>

                {planInfo && planInfo.itemLimit != null ? (
                  <div className="text-xs text-gray-600">Remaining slots: <strong>{Math.max(0, (planInfo.itemLimit || 0) - (products.length || 0))}</strong></div>
                ) : (
                  <div className="text-xs text-gray-600">Unlimited items</div>
                )}

                {planInfo && planInfo.itemLimit != null && products.length >= planInfo.itemLimit && (
                  <Link to="/subscription" className="text-xs text-blue-600 underline">Upgrade</Link>
                )}
              </div>
            )}
          </div>

          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-900">ID</th>
                <th className="px-4 py-2 text-left font-medium text-gray-900">Product</th>
                <th className="px-4 py-2 text-left font-medium text-gray-900">Purchase Cost</th>
                <th className="px-4 py-2 text-left font-medium text-gray-900">Sales Price</th>
                <th className="px-4 py-2 text-left font-medium text-gray-900">Stock</th>
                <th className="px-4 py-2 text-left font-medium text-gray-900">Availability</th>
                <th className="px-4 py-2 text-left font-medium text-gray-900">Action</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {products.map((element, index) => (
              <tr key={element.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-700">{index + 1}</td>
                  <td className="px-4 py-2 text-gray-900 font-medium">{element.name}</td>
                  <td className="px-4 py-2 text-gray-700">{formatCurrency(element.purchaseCost)}</td>
                  <td className="px-4 py-2 text-gray-700">{formatCurrency(element.salePrice)}</td>
                  <td className="px-4 py-2 text-gray-700">{element.stock}</td>
                  <td className="px-4 py-2 text-gray-700">
                    {element.stock > 0 ? (
                      <span className="text-green-600 font-medium">In Stock</span>
                    ) : (
                      <span className="text-red-600 font-medium">Out of Stock</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-700">
                    {can(Permissions.Edit) && (
                      <button
                        className="text-green-700 hover:text-green-900 font-medium mr-2"
                        onClick={() => addProductModalSetting(element)}
                      >
                        Edit
                      </button>
                    )}
                    <button
                      className="text-blue-600 hover:text-red-800 font-medium mr-2"
                      onClick={() => showProductDetails(element)}
                    >
                      Details
                    </button>
                    {can(Permissions.Delete) && (
                      <button
                        className="text-red-600 hover:text-red-800 font-medium"
                        onClick={() => deleteItem(element.id)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

export default Inventory;
